import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const TOKEN_KEY = "auth_token";
const USER_KEY = "auth_user";

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthContextType {
  currentUser: AuthUser | null;
  token: string | null;
  loading: boolean;
  signIn: (token: string, user?: Partial<AuthUser>) => void;
  signOut: () => void;
  getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Decode JWT token to extract payload
const decodeToken = (token: string): { uid: string; exp: number } | null => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(jsonPayload);
    return {
      uid: payload.user_id || payload.sub || payload.uid,
      exp: payload.exp,
    };
  } catch (e) {
    console.error("Failed to decode token:", e);
    return null;
  }
};

// Check if token is expired
const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  // Add 60 second buffer
  return decoded.exp * 1000 < Date.now() + 60000;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load auth state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && !isTokenExpired(storedToken)) {
      setToken(storedToken);
      if (storedUser) {
        try {
          setCurrentUser(JSON.parse(storedUser));
        } catch (e) {
          // If user data is corrupted, extract from token
          const decoded = decodeToken(storedToken);
          if (decoded) {
            setCurrentUser({
              uid: decoded.uid,
              email: null,
              displayName: null,
              photoURL: null,
            });
          }
        }
      } else {
        // Extract user from token
        const decoded = decodeToken(storedToken);
        if (decoded) {
          setCurrentUser({
            uid: decoded.uid,
            email: null,
            displayName: null,
            photoURL: null,
          });
        }
      }
    } else if (storedToken) {
      // Token expired, clear storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }

    setLoading(false);
  }, []);

  const signIn = useCallback((newToken: string, user?: Partial<AuthUser>) => {
    const decoded = decodeToken(newToken);
    if (!decoded) {
      console.error("Invalid token provided to signIn");
      return;
    }

    const authUser: AuthUser = {
      uid: decoded.uid,
      email: user?.email || null,
      displayName: user?.displayName || null,
      photoURL: user?.photoURL || null,
    };

    setToken(newToken);
    setCurrentUser(authUser);
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(authUser));
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setCurrentUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    if (!token) {
      return "";
    }
    if (isTokenExpired(token)) {
      // Token expired, sign out
      signOut();
      return "";
    }
    return token;
  }, [token, signOut]);

  const value = {
    currentUser,
    token,
    loading,
    signIn,
    signOut,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
